import React, { useState, useEffect, useRef } from 'react';
import { FileText, Images, X } from 'lucide-react';
import { boardsAPI } from '../api';
import NoteAttachmentCard from './NoteAttachmentCard';

const OwnerNotesSection = ({ notes, boardId, isExpired, onNoteAdded, brandName = '', clientName = '', participants = [], t = {}, documents = [], images = [] }) => {
  const [newNote, setNewNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  // Rich attachment composer state
  const [pendingAttachment, setPendingAttachment] = useState(null);
  const [showDocPicker, setShowDocPicker] = useState(false);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [selectedImageIds, setSelectedImageIds] = useState([]);

  // Chat-style internal scroll — fixed max height so the editor page
  // doesn't grow with the conversation. Pinned to newest message.
  const notesEndRef = useRef(null);
  const notesListRef = useRef(null);
  const prevNotesCountRef = useRef(notes.length);
  useEffect(() => {
    const el = notesListRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const el = notesListRef.current;
    if (notes.length > prevNotesCountRef.current && el) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
    prevNotesCountRef.current = notes.length;
  }, [notes.length]);

  // "Seen" receipts — when the owner actually views the conversation
  // panel, mark client messages as seen (fires once per mount).
  const sectionRootRef = useRef(null);
  const seenSentRef = useRef(false);
  useEffect(() => {
    const el = sectionRootRef.current;
    if (!el || seenSentRef.current) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && !seenSentRef.current) {
        seenSentRef.current = true;
        boardsAPI.markNotesSeen(boardId).catch(() => {});
        obs.disconnect();
      }
    }, { threshold: 0.2 });
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardId]);
  const lastOwnerNote = [...notes].reverse().find((n) => n.author_type === 'owner');

  const handleSubmit = async () => {
    if (!newNote.trim() && !pendingAttachment) return;

    setSubmitting(true);
    try {
      await boardsAPI.addNote(boardId, newNote.trim(), pendingAttachment);
      setNewNote('');
      setPendingAttachment(null);
      onNoteAdded();
    } catch (err) {
      console.error('Error adding note:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditStart = (note) => {
    setEditingNoteId(note.id);
    setEditText(note.text);
    setDeleteConfirmId(null);
  };

  const handleEditCancel = () => {
    setEditingNoteId(null);
    setEditText('');
  };

  const handleEditSave = async (noteId) => {
    if (!editText.trim()) return;
    
    setEditSubmitting(true);
    try {
      await boardsAPI.editNote(boardId, noteId, editText.trim());
      setEditingNoteId(null);
      setEditText('');
      onNoteAdded();
    } catch (err) {
      console.error('Error editing note:', err);
    } finally {
      setEditSubmitting(false);
    }
  };

  const handleDeleteConfirm = (noteId) => {
    setDeleteConfirmId(noteId);
    setEditingNoteId(null);
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmId(null);
  };

  const handleDelete = async (noteId) => {
    try {
      await boardsAPI.deleteNote(boardId, noteId);
      setDeleteConfirmId(null);
      onNoteAdded();
    } catch (err) {
      console.error('Error deleting note:', err);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div ref={sectionRootRef} data-testid="owner-notes-section" style={{ 
      marginTop: '48px',
      paddingTop: '32px',
      borderTop: '1px solid var(--kahla-border)'
    }}>
      <h2 style={{ 
        fontFamily: 'Playfair Display, serif',
        fontSize: '24px',
        color: 'var(--kahla-charcoal)',
        marginBottom: '8px'
      }}>
        {t.notes_title || 'Notes & Thoughts'}
      </h2>
      <p style={{ 
        fontSize: '13px',
        color: 'var(--kahla-text-light)',
        marginBottom: '24px'
      }}>
        {t.shared_communication_space || t.notes_subtitle || 'Shared communication space with your client'}
      </p>

      {/* Notes List */}
      <div>
        {notes.length === 0 ? (
          <p style={{ 
            color: 'var(--kahla-text-light)',
            padding: '24px',
            backgroundColor: 'var(--kahla-white)',
            border: '1px solid var(--kahla-border)',
            textAlign: 'center'
          }}>
            {t.no_notes || 'No notes yet. Add a note to start a reflective conversation.'}
          </p>
        ) : (
          <div
            ref={notesListRef}
            className="cv-chat-scroll"
            data-testid="owner-notes-scroll-container"
            style={{
              display: 'flex', flexDirection: 'column', gap: '12px',
              maxHeight: '480px', overflowY: 'auto',
              padding: '4px 10px 4px 2px',
            }}
          >
            {notes.map((note) => (
              <div 
                key={note.id}
                data-testid={`note-${note.id}`}
                style={{
                  backgroundColor: note.author_type === 'owner' ? 'var(--kahla-cream-light)' : 'var(--kahla-white)',
                  border: `1px solid ${note.author_type === 'owner' ? 'var(--kahla-beige)' : 'var(--kahla-border)'}`,
                  padding: '16px 20px'
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '10px'
                }}>
                  <span style={{ 
                    fontSize: '10px',
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: note.author_type === 'owner' ? 'var(--kahla-taupe)' : 'var(--kahla-text-light)',
                    fontWeight: '500',
                    backgroundColor: note.author_type === 'owner' ? 'var(--kahla-cream)' : 'var(--kahla-cream-light)',
                    padding: '4px 8px'
                  }}>
                    {note.author_type === 'owner'
                      ? ((t.from_studio || 'From {brand}').replace('{brand}', brandName || t.studio_name || 'Studio'))
                      : (() => {
                          if (typeof note.participant_idx === 'number' && participants[note.participant_idx]) {
                            return (t.from_named || 'From {name}').replace('{name}', participants[note.participant_idx]);
                          }
                          if (clientName && clientName.trim()) {
                            return (t.from_named || 'From {name}').replace('{name}', clientName.trim());
                          }
                          return (t.from_client_default || 'From Client');
                        })()}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ 
                      fontSize: '11px',
                      color: 'var(--kahla-text-light)'
                    }}>
                      {formatDate(note.created_at)}
                      {note.updated_at && note.updated_at !== note.created_at && (
                        <span style={{ fontStyle: 'italic' }}> {t.edited || '(edited)'}</span>
                      )}
                    </span>
                    {note.author_type === 'owner' && !isExpired && (
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          data-testid={`edit-note-${note.id}`}
                          onClick={() => handleEditStart(note)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '11px',
                            color: 'var(--kahla-taupe)',
                            padding: '2px 6px',
                            opacity: 0.7,
                            transition: 'opacity 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.opacity = 1}
                          onMouseLeave={(e) => e.target.style.opacity = 0.7}
                        >
                          {t.edit || 'Edit'}
                        </button>
                        <button
                          data-testid={`delete-note-${note.id}`}
                          onClick={() => handleDeleteConfirm(note.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '11px',
                            color: '#c0392b',
                            padding: '2px 6px',
                            opacity: 0.7,
                            transition: 'opacity 0.2s'
                          }}
                          onMouseEnter={(e) => e.target.style.opacity = 1}
                          onMouseLeave={(e) => e.target.style.opacity = 0.7}
                        >
                          {t.delete || 'Delete'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Delete Confirmation */}
                {deleteConfirmId === note.id && (
                  <div style={{
                    backgroundColor: '#fef3f2',
                    border: '1px solid #fecaca',
                    padding: '12px 16px',
                    marginBottom: '12px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span style={{ fontSize: '13px', color: '#991b1b' }}>
                      {t.delete_note_confirm || 'Delete this note?'}
                    </span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        data-testid={`confirm-delete-${note.id}`}
                        onClick={() => handleDelete(note.id)}
                        style={{
                          padding: '6px 12px',
                          fontSize: '12px',
                          backgroundColor: '#dc2626',
                          color: 'white',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        {t.yes_delete || 'Yes, delete'}
                      </button>
                      <button
                        data-testid={`cancel-delete-${note.id}`}
                        onClick={handleDeleteCancel}
                        style={{
                          padding: '6px 12px',
                          fontSize: '12px',
                          backgroundColor: 'white',
                          color: 'var(--kahla-text)',
                          border: '1px solid var(--kahla-border)',
                          cursor: 'pointer'
                        }}
                      >
                        {t.cancel || 'Cancel'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Edit Mode */}
                {editingNoteId === note.id ? (
                  <div>
                    <textarea
                      data-testid={`edit-textarea-${note.id}`}
                      value={editText}
                      onChange={(e) => setEditText(e.target.value.slice(0, 1000))}
                      style={{
                        width: '100%',
                        minHeight: '80px',
                        padding: '12px',
                        border: '1px solid var(--kahla-border)',
                        backgroundColor: 'var(--kahla-white)',
                        fontSize: '14px',
                        resize: 'none',
                        fontFamily: 'Inter, sans-serif'
                      }}
                    />
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginTop: '10px'
                    }}>
                      <span style={{ fontSize: '11px', color: 'var(--kahla-text-light)' }}>
                        {editText.length}/1000
                      </span>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          data-testid={`cancel-edit-${note.id}`}
                          onClick={handleEditCancel}
                          className="btn-secondary"
                          style={{ padding: '8px 16px', fontSize: '12px' }}
                        >
                          {t.cancel || 'Cancel'}
                        </button>
                        <button
                          data-testid={`save-edit-${note.id}`}
                          onClick={() => handleEditSave(note.id)}
                          disabled={!editText.trim() || editSubmitting}
                          className="btn-primary"
                          style={{ padding: '8px 16px', fontSize: '12px' }}
                        >
                          {editSubmitting ? (t.saving || 'Saving...') : (t.save || 'Save')}
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    {note.text && (
                      <p style={{ 
                        fontSize: '14px',
                        lineHeight: '1.6',
                        color: 'var(--kahla-text)',
                        whiteSpace: 'pre-wrap'
                      }}>
                        {note.text}
                      </p>
                    )}
                    {note.attachment && (
                      <NoteAttachmentCard attachment={note.attachment} documents={documents} images={images} t={t} />
                    )}
                    {note.id === lastOwnerNote?.id && lastOwnerNote?.seen_by_client_at && (
                      <p data-testid="note-seen-indicator" style={{
                        fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase',
                        fontStyle: 'italic', color: 'var(--kahla-text-light)',
                        textAlign: 'right', margin: '6px 0 0', opacity: 0.8,
                      }}>
                        {t.seen_label || 'Seen'}
                      </p>
                    )}
                  </>
                )}
              </div>
            ))}
            <div ref={notesEndRef} aria-hidden="true" />
          </div>
        )}
      </div>

      {/* Add Note Form — placed below the conversation so the most recent
          messages stay visible while you type a reply (mirrors Client View) */}
      {!isExpired && (
        <div style={{
          backgroundColor: 'var(--kahla-cream-light)',
          border: '1px solid var(--kahla-border)',
          padding: '20px',
          marginTop: '24px'
        }}>
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value.slice(0, 1000))}
            placeholder={t.note_placeholder || 'Add a note or direction for your client.'}
            style={{
              width: '100%',
              minHeight: '80px',
              padding: '12px',
              border: '1px solid var(--kahla-border)',
              backgroundColor: 'var(--kahla-white)',
              fontSize: '14px',
              resize: 'none',
              fontFamily: 'Inter, sans-serif'
            }}
          />
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '12px'
          }}>
            <span style={{ fontSize: '11px', color: 'var(--kahla-text-light)' }}>
              {newNote.length}/1000
            </span>
            <button
              className="btn-primary"
              onClick={handleSubmit}
              disabled={(!newNote.trim() && !pendingAttachment) || submitting}
              style={{ padding: '10px 24px' }}
              data-testid="owner-add-note-btn"
            >
              {submitting ? (t.adding || 'Adding...') : (t.add_note_btn || t.add_note || 'Add Note')}
            </button>
          </div>

          {/* Pending attachment preview */}
          {pendingAttachment && (
            <div style={{ position: 'relative', marginTop: '10px' }} data-testid="pending-attachment">
              <NoteAttachmentCard attachment={pendingAttachment} documents={documents} images={images} t={t} />
              <button
                type="button"
                onClick={() => setPendingAttachment(null)}
                data-testid="pending-attachment-remove"
                style={{
                  position: 'absolute', top: '4px', right: '4px',
                  width: '22px', height: '22px', borderRadius: '50%',
                  background: 'rgba(42,38,32,0.65)', border: 'none', cursor: 'pointer',
                  color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                }}
              ><X size={12} /></button>
            </div>
          )}

          {/* Attach bar — "Dołącz z:" documents / board images */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '11px', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--kahla-text-light)' }}>
              {t.attach_from || 'Attach:'}
            </span>
            <button
              type="button"
              onClick={() => { setShowDocPicker((v) => !v); setShowImagePicker(false); }}
              disabled={!documents.length}
              data-testid="attach-document-btn"
              title={!documents.length ? (t.no_documents_yet || 'No documents on this board yet') : undefined}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                background: showDocPicker ? 'var(--kahla-cream)' : 'transparent',
                border: '1px solid var(--kahla-border)', padding: '6px 12px',
                cursor: documents.length ? 'pointer' : 'not-allowed', fontFamily: 'inherit',
                fontSize: '12px', color: 'var(--kahla-text)', opacity: documents.length ? 1 : 0.5,
              }}
            ><FileText size={13} /> {t.attach_document || 'Document'}</button>
            <button
              type="button"
              onClick={() => { setShowImagePicker((v) => !v); setShowDocPicker(false); setSelectedImageIds([]); }}
              disabled={!images.length}
              data-testid="attach-gallery-btn"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                background: showImagePicker ? 'var(--kahla-cream)' : 'transparent',
                border: '1px solid var(--kahla-border)', padding: '6px 12px',
                cursor: images.length ? 'pointer' : 'not-allowed', fontFamily: 'inherit',
                fontSize: '12px', color: 'var(--kahla-text)', opacity: images.length ? 1 : 0.5,
              }}
            ><Images size={13} /> {t.attach_gallery || 'Board images'}</button>
          </div>

          {/* Document picker */}
          {showDocPicker && (
            <div style={{ marginTop: '10px', background: 'var(--kahla-white)', border: '1px solid var(--kahla-border)', maxHeight: '180px', overflowY: 'auto' }} data-testid="attach-document-picker">
              {documents.map((doc) => (
                <button
                  key={doc.id}
                  type="button"
                  onClick={() => {
                    setPendingAttachment({ type: 'document', document_id: doc.id, filename: doc.title || doc.filename });
                    setShowDocPicker(false);
                  }}
                  data-testid={`attach-doc-option-${doc.id}`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
                    background: 'transparent', border: 'none', borderBottom: '1px solid var(--kahla-border)',
                    padding: '10px 14px', cursor: 'pointer', fontFamily: 'inherit',
                    fontSize: '13px', color: 'var(--kahla-text)', textAlign: 'left',
                  }}
                >
                  <FileText size={14} style={{ flexShrink: 0, color: 'var(--kahla-taupe)' }} />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.title || doc.filename}</span>
                </button>
              ))}
            </div>
          )}

          {/* Board-image picker (max 6) */}
          {showImagePicker && (
            <div style={{ marginTop: '10px', background: 'var(--kahla-white)', border: '1px solid var(--kahla-border)', padding: '12px' }} data-testid="attach-gallery-picker">
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))', gap: '8px', maxHeight: '220px', overflowY: 'auto' }}>
                {images.map((img) => {
                  const selected = selectedImageIds.includes(img.id);
                  return (
                    <button
                      key={img.id}
                      type="button"
                      onClick={() => setSelectedImageIds((prev) => selected
                        ? prev.filter((i) => i !== img.id)
                        : (prev.length >= 6 ? prev : [...prev, img.id]))}
                      data-testid={`attach-img-option-${img.id}`}
                      style={{
                        position: 'relative', aspectRatio: '1', padding: 0, cursor: 'pointer',
                        border: selected ? '2px solid var(--kahla-gold, #C9A96E)' : '1px solid var(--kahla-border)',
                        background: 'var(--kahla-cream-light)', overflow: 'hidden',
                      }}
                    >
                      <img src={img.image_thumb || img.image_data} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', opacity: selected ? 0.85 : 1 }} />
                    </button>
                  );
                })}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
                <span style={{ fontSize: '11px', color: 'var(--kahla-text-light)' }}>{selectedImageIds.length}/6</span>
                <button
                  type="button"
                  onClick={() => {
                    if (selectedImageIds.length) {
                      setPendingAttachment({ type: 'gallery', image_ids: selectedImageIds });
                    }
                    setShowImagePicker(false);
                  }}
                  disabled={!selectedImageIds.length}
                  data-testid="attach-gallery-confirm"
                  className="btn-secondary"
                  style={{ padding: '6px 16px', fontSize: '12px' }}
                >{t.attach_confirm || 'Attach'}</button>
              </div>
            </div>
          )}
        </div>
      )}

      {isExpired && (
        <p style={{ 
          fontSize: '12px',
          color: 'var(--kahla-text-light)',
          fontStyle: 'italic',
          marginTop: '16px',
          textAlign: 'center'
        }}>
          {t.notes_closed || 'This board is archived. Notes are read-only.'}
        </p>
      )}
    </div>
  );
};

export default OwnerNotesSection;
